import type { BadgeVariant } from '../../constants/statusConfig';
import { getStatusConfig } from '../../constants/statusConfig';
import {
  type AttentionItem,
  sortAttention,
} from '../../components/common/detailChrome';
import { formatRelativeTime, formatVersionLabel } from '../../lib/formatters';
import type { SuperDappDetailData, SuperDappTabKey } from './types';

/**
 * Pure derivations for the dapp detail page: what state the app is in, what
 * needs an admin's attention, and a single merged activity timeline. Kept free
 * of React so the rules are testable on their own.
 */

export type Release = SuperDappDetailData['releases'][number];
export type Ticket = SuperDappDetailData['hubspotTickets'][number];

/** `undefined` = still loading, `null` = the Store has not ingested the app. */
export type StoreStatus = 'published' | 'unpublished' | null | undefined;

/** Props for a `StatusBadge` describing the dApp Store state. */
export function describeStoreStatus(status: Exclude<StoreStatus, undefined>): {
  status: string;
  variant: BadgeVariant;
} {
  if (status === 'published') {
    return { status: 'Live in Store', variant: 'success' };
  }
  if (status === 'unpublished') {
    return { status: 'Not in Store', variant: 'warning' };
  }
  return { status: 'Not ingested', variant: 'default' };
}

export const versionLabel = formatVersionLabel;

export function releaseVersion(release: Release): string {
  return versionLabel(release.versionName, release.versionCode);
}

/** HubSpot ticket dates arrive as Date or ISO string depending on transport. */
export function ticketTime(
  value: Date | string | null | undefined,
): number | null {
  if (value == null) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function findReleaseTicket(
  release: Release,
  tickets: Ticket[],
): Ticket | undefined {
  return tickets.find(
    (ticket) =>
      ticket.releaseId === release.id ||
      (release.hubspotTicketId !== null &&
        ticket.hubspotId === release.hubspotTicketId),
  );
}

/** Deep link into the tickets queue: by internal id when we have the row, else by search. */
export function ticketQueuePath(params: {
  ticketId?: string | null;
  search?: string | null;
}): string {
  const query = new URLSearchParams();
  if (params.ticketId) query.set('ticketId', params.ticketId);
  else if (params.search) query.set('search', params.search);
  const encoded = query.toString();
  return encoded ? `/tickets?${encoded}` : '/tickets';
}

export function releaseTicketPath(
  release: Release,
  tickets: Ticket[],
): string | null {
  const ticket = findReleaseTicket(release, tickets);
  if (ticket) return ticketQueuePath({ ticketId: ticket.id });
  if (release.hubspotTicketId) {
    return ticketQueuePath({ search: release.hubspotTicketId });
  }
  return null;
}

export type ReleaseStage =
  | 'failed'
  | 'processing'
  | 'rejected'
  | 'changes'
  | 'in_review'
  | 'approved'
  | 'published'
  | 'archived'
  | 'processed';

export function releaseStage(release: Release): ReleaseStage {
  if (
    release.status === 'ProcessingFailed' ||
    release.submissionStatus === 'ProcessingFailed'
  ) {
    return 'failed';
  }
  if (release.status === 'Processing') return 'processing';
  if (release.status === 'Published') return 'published';
  if (release.status === 'Archived') return 'archived';
  if (
    release.status === 'Rejected' ||
    release.submissionStatus === 'Rejected' ||
    release.rejectedAt !== null
  ) {
    return 'rejected';
  }
  if (release.submissionStatus === 'RequiresChanges') return 'changes';
  if (release.submissionStatus === 'Approved') return 'approved';
  if (
    release.submissionStatus === 'Submitted' ||
    release.submissionStatus === 'UnderReview' ||
    release.submissionStatus === 'AlphaTest'
  ) {
    return 'in_review';
  }
  return 'processed';
}

export interface ReleaseStep {
  label: string;
  at: number | null;
  state: 'done' | 'current' | 'pending' | 'failed';
}

/**
 * The review pipeline as a stepper. A step with no timestamp still counts as
 * done when a later step happened (legacy rows lack intermediate dates).
 */
export function releaseSteps(release: Release): ReleaseStep[] {
  const failed = release.status === 'ProcessingFailed';
  const rejected =
    release.rejectedAt !== null || release.submissionStatus === 'Rejected';
  const raw: Array<Pick<ReleaseStep, 'label' | 'at'>> = failed
    ? [
        { label: 'Uploaded', at: release.created },
        {
          label: 'Processing failed',
          at: release.processedAt ?? release.updated,
        },
      ]
    : [
        { label: 'Uploaded', at: release.created },
        { label: 'Processed', at: release.processedAt },
        { label: 'Submitted', at: release.submittedAt },
        { label: 'In review', at: release.reviewStartedAt },
        { label: 'Reviewed', at: release.reviewCompletedAt },
        rejected
          ? { label: 'Rejected', at: release.rejectedAt }
          : {
              label: 'Published',
              at:
                release.publishedAt ??
                (release.status === 'Published'
                  ? (release.updated ?? release.created)
                  : null),
            },
      ];
  const lastDone = raw.reduce(
    (last, step, index) => (step.at !== null ? index : last),
    -1,
  );
  const terminal = failed || rejected ? raw.length - 1 : -1;

  return raw.map((step, index) => ({
    ...step,
    state:
      index === terminal
        ? 'failed'
        : index <= lastDone
          ? 'done'
          : index === lastDone + 1
            ? 'current'
            : 'pending',
  }));
}

export type DappAttentionItem = AttentionItem<SuperDappTabKey>;

export interface AttentionContext {
  storeStatus: StoreStatus;
  categoryCount: number;
  isAdmin: boolean;
  now?: number;
}

const HOUR = 60 * 60 * 1000;
const WEEK = 7 * 24 * HOUR;

export const STORE_LISTING_ANCHOR = 'store-listing';

/** What an admin should look at first, most severe first. Empty means all clear. */
export function deriveAttention(
  data: SuperDappDetailData,
  context: AttentionContext,
): DappAttentionItem[] {
  const now = context.now ?? Date.now();
  const items: DappAttentionItem[] = [];
  const latest = data.releases[0];

  if (latest) {
    const version = releaseVersion(latest);
    const ticketPath = releaseTicketPath(latest, data.hubspotTickets);
    const reviewAction = ticketPath
      ? { label: 'Open ticket', href: ticketPath }
      : { label: 'Releases', tab: 'releases' as const };
    const releasesAction = { label: 'Releases', tab: 'releases' as const };

    switch (releaseStage(latest)) {
      case 'failed':
        items.push({
          id: 'release-failed',
          tone: 'error',
          title: `${version} failed processing`,
          detail: latest.processingError,
          action: releasesAction,
        });
        break;
      case 'processing':
        if (now - latest.created > HOUR) {
          items.push({
            id: 'release-processing',
            tone: 'warning',
            title: `${version} is still processing`,
            detail: `Uploaded ${formatRelativeTime(latest.created)}`,
            action: releasesAction,
          });
        }
        break;
      case 'rejected':
        items.push({
          id: 'release-rejected',
          tone: 'error',
          title: `${version} was rejected`,
          detail: latest.rejectionReason,
          action: reviewAction,
        });
        break;
      case 'changes':
        items.push({
          id: 'release-changes',
          tone: 'warning',
          title: `${version} requires changes`,
          detail: latest.rejectionReason,
          action: reviewAction,
        });
        break;
      case 'in_review':
        items.push({
          id: 'release-review',
          tone: 'info',
          title: `${version} is ${getStatusConfig('submission', latest.submissionStatus).label.toLowerCase()}`,
          detail: latest.submittedAt
            ? `Submitted ${formatRelativeTime(latest.submittedAt)}`
            : null,
          action: reviewAction,
        });
        break;
      default:
        break;
    }
  }

  const storeAction = {
    label: 'Store listing',
    href: `#${STORE_LISTING_ANCHOR}`,
  };

  if (
    data.dapp.status === 'Published' &&
    context.storeStatus === 'unpublished'
  ) {
    items.push({
      id: 'store-unpublished',
      tone: 'warning',
      title: 'Approved in the Portal but not live in the dApp Store',
      action: storeAction,
    });
  }

  if (
    context.storeStatus !== undefined &&
    context.storeStatus !== null &&
    context.categoryCount === 0
  ) {
    items.push({
      id: 'store-category',
      tone: 'warning',
      title: 'No dApp Store category assigned',
      action: storeAction,
    });
  }

  const kyb = data.publisher.kybSubmission;
  if (context.isAdmin && kyb && kyb.status !== 'Approved') {
    items.push({
      id: 'kyb',
      tone: 'warning',
      title: `Publisher KYB is ${getStatusConfig('kyb', kyb.status).label.toLowerCase()}`,
      detail: kyb.rejectionReason ?? kyb.additionalInfoRequested,
      action: { label: 'Publisher', tab: 'publisher' },
    });
  }

  const recentFailedSubmissions = data.hubspotSubmissions.filter(
    (submission) =>
      !submission.succeeded && now - submission.submittedAt < WEEK,
  ).length;
  if (recentFailedSubmissions > 0) {
    items.push({
      id: 'submissions-failed',
      tone: 'error',
      title: `${recentFailedSubmissions} HubSpot ${recentFailedSubmissions === 1 ? 'submission' : 'submissions'} failed this week`,
      action: { label: 'Activity', tab: 'activity' },
    });
  }

  const recentFailedEmails = data.emails.filter(
    (email) =>
      email.status === 'Failed' &&
      now - (email.failedAt ?? email.created) < WEEK,
  ).length;
  if (recentFailedEmails > 0) {
    items.push({
      id: 'emails-failed',
      tone: 'warning',
      title: `${recentFailedEmails} ${recentFailedEmails === 1 ? 'email' : 'emails'} failed to send this week`,
      action: { label: 'Activity', tab: 'activity' },
    });
  }

  return sortAttention(items);
}

export type ActivityKind =
  | 'release'
  | 'ticket'
  | 'alert'
  | 'email'
  | 'submission'
  | 'webhook'
  | 'event';

export const ACTIVITY_KIND_LABEL: Record<ActivityKind, string> = {
  release: 'Releases',
  ticket: 'Tickets',
  alert: 'Alerts',
  email: 'Emails',
  submission: 'HubSpot',
  webhook: 'Webhooks',
  event: 'Events',
};

export const ACTIVITY_KINDS = Object.keys(
  ACTIVITY_KIND_LABEL,
) as ActivityKind[];

export type ReleaseMilestone =
  | 'uploaded'
  | 'submitted'
  | 'published'
  | 'rejected'
  | 'failed';

export type ActivityEntry = { id: string; at: number } & (
  | { kind: 'release'; release: Release; milestone: ReleaseMilestone }
  | { kind: 'ticket'; ticket: Ticket; milestone: 'opened' | 'closed' }
  | { kind: 'alert'; alert: SuperDappDetailData['alerts'][number] }
  | { kind: 'email'; email: SuperDappDetailData['emails'][number] }
  | {
      kind: 'submission';
      submission: SuperDappDetailData['hubspotSubmissions'][number];
    }
  | { kind: 'webhook'; log: SuperDappDetailData['webhookLogs'][number] }
  | { kind: 'event'; event: SuperDappDetailData['events'][number] }
);

/** Everything that happened to this dapp, newest first. */
export function buildActivity(
  data: SuperDappDetailData,
  options: { includeEvents: boolean },
): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  for (const release of data.releases) {
    const push = (milestone: ReleaseMilestone, at: number | null) => {
      if (at !== null) {
        entries.push({
          kind: 'release',
          id: `release-${release.id}-${milestone}`,
          at,
          release,
          milestone,
        });
      }
    };
    push('uploaded', release.created);
    push('submitted', release.submittedAt);
    if (release.status === 'ProcessingFailed') {
      push('failed', release.processedAt ?? release.updated ?? release.created);
    }
    push('published', release.publishedAt);
    push('rejected', release.rejectedAt);
  }

  for (const ticket of data.hubspotTickets) {
    const opened = ticketTime(ticket.createdAt);
    if (opened !== null) {
      entries.push({
        kind: 'ticket',
        id: `ticket-${ticket.id}-opened`,
        at: opened,
        ticket,
        milestone: 'opened',
      });
    }
    const closed = ticketTime(ticket.closedDate);
    if (closed !== null) {
      entries.push({
        kind: 'ticket',
        id: `ticket-${ticket.id}-closed`,
        at: closed,
        ticket,
        milestone: 'closed',
      });
    }
  }

  for (const alert of data.alerts) {
    entries.push({
      kind: 'alert',
      id: `alert-${alert.id}`,
      at: alert.created,
      alert,
    });
  }
  for (const email of data.emails) {
    entries.push({
      kind: 'email',
      id: `email-${email.id}`,
      at: email.sentAt ?? email.created,
      email,
    });
  }
  for (const submission of data.hubspotSubmissions) {
    entries.push({
      kind: 'submission',
      id: `submission-${submission.id}`,
      at: submission.submittedAt,
      submission,
    });
  }
  for (const log of data.webhookLogs) {
    entries.push({
      kind: 'webhook',
      id: `webhook-${log.id}`,
      at: log.receivedAt,
      log,
    });
  }
  if (options.includeEvents) {
    for (const event of data.events) {
      entries.push({
        kind: 'event',
        id: `event-${event.id}`,
        at: event.created,
        event,
      });
    }
  }

  return entries.sort((a, b) => b.at - a.at);
}
